import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchShop, fetchHairdresser, createHairdresser, updateHairdresser } from "../api.js";
import {
  buildHairdresserImagePath,
  supabaseConfigured,
  uploadImage,
} from "../supabaseClient.js";
import { ImageCropper } from "../components/ImageCropper";
import { Button } from "../components/ui/button.jsx";
import { ArrowLeft } from "lucide-react";

const DAY_NAMES = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

function defaultRange() {
  return { startTime: "09:00", endTime: "17:00" };
}

function defaultWorkingHours() {
  return DAY_NAMES.map((_, i) => ({
    enabled: i < 5,
    ranges: [defaultRange()],
  }));
}

function timeToMinutes(time) {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function minutesToTime(minutes) {
  const h = String(Math.floor(minutes / 60)).padStart(2, "0");
  const m = String(minutes % 60).padStart(2, "0");
  return `${h}:${m}`;
}

function hoursFromApi(apiHours) {
  const schedule = defaultWorkingHours().map((d) => ({ ...d, enabled: false, ranges: [] }));
  for (const wh of apiHours) {
    const day = schedule[wh.weekday];
    day.enabled = true;
    day.ranges.push({
      startTime: minutesToTime(wh.start_minute),
      endTime: minutesToTime(wh.end_minute),
    });
  }
  // Ensure enabled days without ranges get a default
  for (const day of schedule) {
    if (day.enabled && day.ranges.length === 0) {
      day.ranges.push(defaultRange());
    }
  }
  return schedule;
}

function hoursToApi(schedule) {
  const result = [];
  schedule.forEach((day, i) => {
    if (day.enabled) {
      for (const r of day.ranges) {
        result.push({ weekday: i, start_minute: timeToMinutes(r.startTime), end_minute: timeToMinutes(r.endTime) });
      }
    }
  });
  return result;
}

export default function HairdresserFormPage() {
  const { orgSlug, shopId, hairdresserId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(hairdresserId);

  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [workingHours, setWorkingHours] = useState(defaultWorkingHours);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const shop = await fetchShop(shopId);
        setAppointmentTypes(shop.appointment_types || []);
        if (isEdit) {
          const h = await fetchHairdresser(shopId, hairdresserId);
          setName(h.name);
          setImageUrl(h.image_url || null);
          setSelectedTypes(h.appointment_type_ids || []);
          if (h.working_hours) {
            setWorkingHours(hoursFromApi(h.working_hours));
          }
        }
      } catch (err) {
        setError(err.response?.data?.detail || "Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shopId, hairdresserId, isEdit]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreview(null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const toggleType = (id) => {
    setSelectedTypes((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setImageFile(null);
      return;
    }
    setPendingFile(file);
    setShowCropper(true);
    e.target.value = "";
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImageUrl(null);
  };

  const handleCropComplete = (croppedFile) => {
    setImageFile(croppedFile);
    setPendingFile(null);
    setShowCropper(false);
  };

  const handleCropCancel = () => {
    setPendingFile(null);
    setShowCropper(false);
  };

  const updateDay = (index, field, value) => {
    setWorkingHours((prev) =>
      prev.map((day, i) => (i === index ? { ...day, [field]: value } : day))
    );
  };

  const updateRange = (dayIndex, rangeIndex, field, value) => {
    setWorkingHours((prev) =>
      prev.map((day, i) => {
        if (i !== dayIndex) return day;
        const ranges = day.ranges.map((r, ri) => (ri === rangeIndex ? { ...r, [field]: value } : r));
        return { ...day, ranges };
      })
    );
  };

  const addRange = (dayIndex) => {
    setWorkingHours((prev) =>
      prev.map((day, i) => {
        if (i !== dayIndex) return day;
        const lastEnd = day.ranges.length > 0 ? day.ranges[day.ranges.length - 1].endTime : "13:00";
        return { ...day, ranges: [...day.ranges, { startTime: lastEnd, endTime: "17:00" }] };
      })
    );
  };

  const removeRange = (dayIndex, rangeIndex) => {
    setWorkingHours((prev) =>
      prev.map((day, i) => {
        if (i !== dayIndex) return day;
        return { ...day, ranges: day.ranges.filter((_, ri) => ri !== rangeIndex) };
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      let nextImageUrl = imageUrl;
      if (imageFile) {
        const path = buildHairdresserImagePath(shopId, imageFile);
        nextImageUrl = await uploadImage({ file: imageFile, path });
      }
      const body = {
        name,
        image_url: nextImageUrl,
        appointment_type_ids: selectedTypes,
        working_hours: hoursToApi(workingHours),
      };
      if (isEdit) {
        await updateHairdresser(shopId, hairdresserId, body);
      } else {
        await createHairdresser(shopId, body);
      }
      navigate(`/admin/${orgSlug}/shops/${shopId}`);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Erro ao guardar profissional");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-background p-6 md:p-12">
        <p className="text-white text-center">A carregar…</p>
      </main>
    );
  }

  const displayImage = imagePreview || imageUrl;

  return (
    <main className="min-h-screen bg-background p-6 md:p-12">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(`/admin/${orgSlug}/shops/${shopId}`)}
          className="flex items-center gap-2 text-white mb-6 hover:text-gold transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar</span>
        </button>

        <header className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gold mb-3">
            {isEdit ? "Editar profissional" : "Novo profissional"}
          </h1>
        </header>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="bg-card border border-border rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <label htmlFor="hairdresser-name" className="block text-sm font-medium text-white">
                Nome *
              </label>
              <input
                id="hairdresser-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
              />
            </div>

            {/* Image */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-white">
                Imagem do profissional
              </label>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="w-32 h-32 rounded-full overflow-hidden border border-border bg-background flex items-center justify-center">
                  {displayImage ? (
                    <img src={displayImage} alt="Imagem do profissional" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-muted text-sm">Sem imagem</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    disabled={!supabaseConfigured || submitting}
                    className="text-sm text-muted file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gold file:text-white hover:file:bg-gold-dark file:cursor-pointer disabled:opacity-50"
                  />
                  {imageUrl && (
                    <Button size="sm" variant="outline" type="button" onClick={handleRemoveImage}>
                      Remover
                    </Button>
                  )}
                  {submitting && imageFile && <span className="text-sm text-muted">A enviar…</span>}
                  {!supabaseConfigured && (
                    <p className="text-xs text-muted">Defina VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY para uploads.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-white">Serviços</label>
              <div className="space-y-2">
                {appointmentTypes.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(t.id)}
                      onChange={() => toggleType(t.id)}
                      className="rounded border-border bg-background text-gold focus:ring-2 focus:ring-gold"
                    />
                    <span className="text-white text-sm">
                      {t.name} ({t.duration_minutes} min)
                    </span>
                  </label>
                ))}
                {appointmentTypes.length === 0 && <p className="text-muted text-sm">Nenhum serviço disponível.</p>}
              </div>
            </div>

            {/* Working hours */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-white">Horário de trabalho</label>
              <div className="space-y-3">
                {workingHours.map((day, i) => (
                  <div
                    key={i}
                    className={`p-4 rounded-lg border ${
                      day.enabled
                        ? "bg-background border-border"
                        : "bg-card border-border/50 opacity-60"
                    }`}
                  >
                    <label className="flex items-center gap-3 mb-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={day.enabled}
                        onChange={(e) => {
                          updateDay(i, "enabled", e.target.checked);
                          if (e.target.checked && day.ranges.length === 0) {
                            addRange(i);
                          }
                        }}
                        className="rounded border-border bg-card text-gold focus:ring-2 focus:ring-gold"
                      />
                      <span className="text-white font-medium">{DAY_NAMES[i]}</span>
                    </label>
                    {day.enabled ? (
                      <div className="space-y-2 ml-7">
                        {day.ranges.map((r, ri) => (
                          <div key={ri} className="flex items-center gap-2">
                            <input
                              type="time"
                              value={r.startTime}
                              onChange={(e) => updateRange(i, ri, "startTime", e.target.value)}
                              className="px-3 py-2 bg-card border border-border rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                            />
                            <span className="text-muted">—</span>
                            <input
                              type="time"
                              value={r.endTime}
                              onChange={(e) => updateRange(i, ri, "endTime", e.target.value)}
                              className="px-3 py-2 bg-card border border-border rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                            />
                            {day.ranges.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRange(i, ri)}
                                className="px-2 py-1 text-red-400 hover:text-red-300 text-lg"
                                title="Remover período"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addRange(i)}
                          className="text-sm text-gold hover:text-gold-light"
                        >
                          + Período
                        </button>
                      </div>
                    ) : (
                      <span className="text-muted text-sm ml-7">Fechado</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button type="submit" className="flex-1" disabled={submitting}>
                {submitting ? "A guardar…" : "Guardar"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/admin/${orgSlug}/shops/${shopId}`)}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </div>
      {showCropper && pendingFile && (
        <ImageCropper
          file={pendingFile}
          cropType="avatar"
          onComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </main>
  );
}
